import { Component, EventEmitter, Input, Output, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '../../../services/storage.service';
import { Angulartics2 } from 'angulartics2';
import moment from 'moment';
import { OPEN_IN_PLANNER_KEY, OpenInPlannerInfo, FeatureFlagDxpService } from '@pure1/data';
import {
    isArrayVersionSupported,
    getSupportedPurityVersion,
    isAgentStreamlined,
    agentStatusUnknown,
} from '../../../utils/cdl-utils';
import { FeatureNames } from '../../../model/FeatureNames';
import { TooltipDirective } from '@pure/hive';

@Component({
    selector: 'capacity-down-licensing-icon',
    templateUrl: './capacity-down-licensing-icon.component.html',
})
export class CapacityDownLicensingIconComponent implements OnInit {
    @ViewChild(TooltipDirective) hiveTooltip: TooltipDirective<any>;

    @Output() closeModal = new EventEmitter<void>();

    @Input() readonly applianceId: string;
    @Input() readonly arrayName: string;
    @Input() readonly arrayVersion: string;
    @Input() readonly agentInstalled: boolean;
    @Input() readonly agentRunningVersion: string;
    @Input() readonly agentRecommendedVersion: string;
    @Input() readonly redirectEnabled: boolean = true;

    cdlExpansionAutomationFF = false;

    constructor(
        private readonly router: Router,
        private storageService: StorageService,
        private angulartics2: Angulartics2,
        private featureFlagDxpService: FeatureFlagDxpService,
    ) {}

    ngOnInit(): void {
        this.featureFlagDxpService.getFeatureFlag(FeatureNames.CDL_EXPANSION_AUTOMATION).subscribe(feature => {
            this.cdlExpansionAutomationFF = feature?.enabled === true;
        });
    }

    upgradeClickHandler(): void {
        if (!this.redirectEnabled) {
            return;
        }

        const info: OpenInPlannerInfo = {
            applianceId: this.applianceId,
            timestamp: moment.now(),
            recommendedUsable: 0,
            originalUsable: 0,
        };
        this.storageService.set(OPEN_IN_PLANNER_KEY, JSON.stringify(info));
        this.router.navigate(['/analysis/planning']);

        this.angulartics2.eventTrack.next({
            action: 'Capacity-Down-Licensing-Icon - continue_to_planning',
            properties: { category: 'Action' },
        });
    }

    /**
     * Returns true if both the array version is supported and the agent is up to date.
     * This indicates that the array is streamlined for automated capacity down licensing using the agent.
     */
    isStreamlined(): boolean {
        return (
            isArrayVersionSupported(this.arrayVersion) &&
            isAgentStreamlined(this.agentInstalled, this.agentRunningVersion, this.agentRecommendedVersion)
        );
    }

    private createTooltipData(
        title: string,
        description: string,
        linkText: string,
        link?: string,
        subText?: string,
    ): TooltipData {
        return { title, description, linkText, link, subText };
    }

    private getIgnoreHint(action: string): string {
        return ` ${action} is highly recommended but you can ignore this message and click to plan capacity expansion.`;
    }

    /**
     * When we hover over the CDL icon, we show a tooltip with information about the current array state.
     * This method constructs the tooltip data based on the array and agent status.
     * @returns An object containing the tooltip title, description, link text, and optional link and subtext.
     */
    getTooltipData(): TooltipData {
        if (!isArrayVersionSupported(this.arrayVersion)) {
            return this.createTooltipData(
                'CDL enabled array - not streamlined',
                `Update your Array to at least Purity ${getSupportedPurityVersion(this.arrayVersion)} to automate and speed up future capacity expansions.`,
                'Update now',
                '/dashboard/software-lifecycle/purity-upgrades',
                this.getIgnoreHint('Update'),
            );
        }

        if (agentStatusUnknown(this.agentInstalled, this.agentRunningVersion, this.agentRecommendedVersion)) {
            return this.createTooltipData(
                'CDL enabled array - agent status unknown',
                'Entitlement agent information is unavailable. You may have view-only access or the system cannot determine the agent status. The agent can automate capacity expansions on demand if installed.',
                'Click to plan capacity expansion.',
            );
        }

        if (!this.agentInstalled) {
            return this.createTooltipData(
                'CDL enabled array - agent not installed',
                'Install the Entitlement agent to automate and speed up future capacity expansions. No action needed until you request an expansion — installing now removes friction later.',
                'Install now',
                '/administration/edgeservice/agents',
                this.getIgnoreHint('Installation'),
            );
        }

        if (this.agentInstalled && this.agentRunningVersion !== this.agentRecommendedVersion) {
            return this.createTooltipData(
                'CDL enabled array - agent not streamlined',
                'Update your Entitlement agent to the latest version to automate and speed up future capacity expansions.',
                'Update now',
                '/administration/edgeservice/agents',
                this.getIgnoreHint('Update'),
            );
        }

        return this.createTooltipData(
            'CDL enabled array - streamlined',
            'Your Array and Entitlement agent are up to date. Gain capacity automatically on demand on this array.',
            'Click to plan capacity expansion.',
        );
    }

    /**
     * Handles the call-to-action click event.
     * Emits a close event (for modals), and navigates to the provided link,
     * If arrayName is provided, it adds a filter query parameter to the navigation.
     *
     * @param link The route to navigate to.
     */
    callToActionClickHandler(link: string): void {
        this.closeModal.emit(); // some pages have this icon inside a modal, emitting close signal to the parent.
        const queryParams = {};
        if (this.arrayName) {
            queryParams['filter'] = JSON.stringify([{ entity: 'arrays', key: 'array_name', value: this.arrayName }]);
        }
        this.router.navigate([link], { queryParams });
    }
}

/**
 * Interface representing the data structure for tooltip information.
 */
export interface TooltipData {
    title: string;
    description: string;
    linkText: string;
    link?: string;
    subText?: string;
}
